import type { ComponentProps } from 'react';
import remarkGfm from 'remark-gfm';
import { Streamdown } from 'streamdown';
import './streamdown.css';

export type AIChatMarkdownVariant = 'chat' | 'assistant';

export type AIChatMarkdownComponents = NonNullable<ComponentProps<typeof Streamdown>['components']>;

interface AIChatMarkdownProps {
  children: string;
  components?: AIChatMarkdownComponents;
  isAnimating?: boolean;
  mode?: ComponentProps<typeof Streamdown>['mode'];
  variant?: AIChatMarkdownVariant;
}

const markdownSizeClasses: Record<AIChatMarkdownVariant, Record<string, string>> = {
  chat: {},
  assistant: {
    h1: 'text-2xl font-bold',
    h2: 'text-xl font-semibold',
    h3: 'text-lg font-semibold',
    h4: 'text-base font-semibold',
    h5: 'text-sm font-semibold',
  },
};

const AIChatMarkdown = ({ children, isAnimating = true, mode = 'streaming', variant = 'chat', components }: AIChatMarkdownProps) => {
  const isAssistant = variant === 'assistant';
  const sizeClasses = isAssistant ? markdownSizeClasses[variant] : {};

  const defaultComponents: AIChatMarkdownComponents = isAssistant
    ? {
        h1: (props) => <h1 {...(props as ComponentProps<'h1'>)} className={sizeClasses.h1} />,
        h2: (props) => <h2 {...(props as ComponentProps<'h2'>)} className={sizeClasses.h2} />,
        h3: (props) => <h3 {...(props as ComponentProps<'h3'>)} className={sizeClasses.h3} />,
        h4: (props) => <h4 {...(props as ComponentProps<'h4'>)} className={sizeClasses.h4} />,
        h5: (props) => <h5 {...(props as ComponentProps<'h5'>)} className={sizeClasses.h5} />,
      }
    : {};

  return (
    <Streamdown
      className='ai-chat-streamdown'
      linkSafety={{ enabled: false }}
      mode={mode}
      isAnimating={isAnimating}
      animated
      remarkPlugins={[remarkGfm]}
      components={{ ...defaultComponents, ...components }}
    >
      {children}
    </Streamdown>
  );
};

export default AIChatMarkdown;
